import React, { useCallback, useState } from "react";
import { Button, CircularProgress, Paper, Typography } from "@mui/material";
import { PlayArrow } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTestExecution } from "../../../utils/testUtils";
import { startTestExecution } from "../../../firebase";
import { useConfig } from "../../../contexts/ConfigContext";
import { TestSuite } from "@finvari/deliver";
import TestStatusChip from "../../../components/status/TestStatusChip";
import TestSubjectSelectionDialog from "./TestSubjectSelectionDialog";

type Props = {
  testSuite: TestSuite;
  environmentId: string;
  rolloutId: string;
  isDeployingOrSetup?: boolean;
  // When set, running tests is disabled (e.g. a pooled environment not leased to the current user).
  // The string is shown as the button tooltip.
  lockReason?: string;
};

export default function TestSuiteCard({ testSuite, environmentId, rolloutId, isDeployingOrSetup = false, lockReason }: Props) {
  const navigate = useNavigate();
  const testSuiteId = testSuite.id;
  const [startingTest, setStartingTest] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { testExecution } = useTestExecution({ testSuiteId, environmentId, rolloutId });
  const { config } = useConfig();
  // The suite's pinned golden baseline, so environment-page runs default to the same reference as
  // PR-panel runs. Without this the environment page ignored the pin.
  const goldenBaselineId = config?.goldenBaselines?.[testSuite.id]?.executionId;
  const isRunning = testExecution?.status === 'pending' || testExecution?.status === 'in_progress' || !!startingTest;

  const handleStartTestExecution = useCallback(async (testSuiteId: string, rolloutIdParam: string, selectedSubjects: string[], baselineExecutionId?: string, comments?: string, acknowledgedDataMismatch?: boolean) => {
    if (startingTest) return;
    setStartingTest(testSuiteId);

    try {
      const result = await startTestExecution(testSuiteId, rolloutIdParam, selectedSubjects, undefined, baselineExecutionId, comments, acknowledgedDataMismatch);
      const resultData = result.data as any;
      navigate(`/environment/${environmentId}/test-execution/${testSuiteId}?executionId=${resultData.executionId}`);
    } catch (error) {
      console.error('Error starting test execution:', error);
      // TODO: Show error message to user
    } finally {
      setStartingTest(null);
    }
  }, [environmentId, navigate, startingTest]);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleConfirmSubjects = (selectedSubjects: string[], baselineExecutionId?: string, comments?: string, acknowledgedDataMismatch?: boolean) => {
    if (rolloutId) {
      handleStartTestExecution(testSuiteId, rolloutId, selectedSubjects, baselineExecutionId, comments, acknowledgedDataMismatch);
      setDialogOpen(false);
    }
  };

  if (!testSuite) return null;

  return (
    <>
      <Paper
        elevation={1}
        sx={{
          p: 3,
          textAlign: 'center',
          ...(testExecution ? {
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          } : {}),
        }}
        onClick={testExecution ? () => navigate(
          `/environment/${environmentId}/test-execution/${testSuite.id}?executionId=${testExecution.executionId}`
        ) : undefined}
      >
        <TestStatusChip status={testExecution?.status} />
        <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
          {testSuite.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {testSuite.description}
        </Typography>
        <Button
          variant="contained"
          startIcon={isRunning ? <CircularProgress size={12} /> : <PlayArrow />}
          disabled={isRunning || isDeployingOrSetup || !!lockReason}
          onClick={(e) => {
            e.stopPropagation();
            if (rolloutId && !isDeployingOrSetup && !lockReason) {
              handleOpenDialog();
            }
          }}
          title={lockReason ?? (isDeployingOrSetup ? 'Wait for deploy/setup to complete' : undefined)}
        >
          {isRunning ? 'Running...' : isDeployingOrSetup ? 'Deploy in progress...' : 'Run Tests'}
        </Button>
      </Paper>

      <TestSubjectSelectionDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        testSuite={testSuite}
        environmentId={environmentId}
        onConfirm={handleConfirmSubjects}
        goldenBaselineId={goldenBaselineId}
      />
    </>
  )
}